import { useState, useEffect, useMemo, useRef } from 'react';
import { motion } from 'framer-motion';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faRotateRight } from '@fortawesome/free-solid-svg-icons';
import { HDate } from '@hebcal/core';
import DraggableMoon from './DraggableMoon';
import InteractiveOrrery from './InteractiveOrrery';
import { getHebrewDate, getMoonPhase, getUpcomingHolidays, HEBREW_MONTHS, numberToHebrewLetters, yearToHebrewLetters, normalizeMonthName } from '../../utils/hebrewCalendar';
import { calculateInitialAngle, normalizeAngle } from '../../utils/calendarHelpers';
import { PRIMARY_COLOR } from '../../constants/colors';

// Gematria tooltip component (from HebrewCalendarHeroMobile)
function GematriaTooltip({ children, explanation }) {
  const [showTooltip, setShowTooltip] = useState(false);
  const [position, setPosition] = useState({ x: 0, y: 0 });

  const handleMouseMove = (e) => {
    setPosition({ x: e.clientX, y: e.clientY });
  };

  return (
    <span
      className="relative inline-block cursor-help border-b border-dotted border-text-secondary"
      onMouseEnter={() => setShowTooltip(true)}
      onMouseLeave={() => setShowTooltip(false)}
      onMouseMove={handleMouseMove}
      onTouchStart={() => setShowTooltip(!showTooltip)}
    >
      {children}
      {showTooltip && (
        <div 
          className="fixed w-64 p-3 bg-white text-text-primary text-xs rounded-lg border border-border shadow-lg z-50 pointer-events-none"
          style={{
            left: `${position.x}px`,
            top: `${position.y + 10}px`,
            transform: 'translateX(-50%)'
          }}
        >
          <div className="font-semibold mb-1 text-text-primary">Gematria (גימטריה)</div>
          <div className="text-text-secondary leading-relaxed">{explanation}</div>
        </div>
      )}
    </span>
  );
}

export default function HebrewCalendarHeroV2() {
  // Use the original Hebrew date system (not angle-based)
  const [hebrewDate, setHebrewDate] = useState(null);
  const [moonPhase, setMoonPhase] = useState(null);
  const [nextHoliday, setNextHoliday] = useState(null);
  const [daysInMonth, setDaysInMonth] = useState(30);
  const [dayOffset, setDayOffset] = useState(0); // Track day offset from moon drag

  // Shared angle state for orrery (will sync with moon drag)
  const [currentAngle, setCurrentAngle] = useState(() => calculateInitialAngle());
  
  // Track previous angle for incremental scrubbing (prevents year wrap-around)
  const previousAngleRef = useRef(calculateInitialAngle());
  const accumulatedDayOffsetRef = useRef(0);
  const cumulativeAngleDeltaRef = useRef(0); // Track cumulative unnormalized angle delta

  // Track if we're showing today's date
  const [isToday, setIsToday] = useState(true);
  
  // Track if this is the initial load (for animation) - only animate on first render
  const [hasAnimated, setHasAnimated] = useState(false);
  
  // Calculate mobile orrery size (100vw + 48px for negative margins)
  const [mobileOrrerySize, setMobileOrrerySize] = useState(400);
  
  useEffect(() => {
    if (typeof window !== 'undefined') {
      const updateSize = () => {
        setMobileOrrerySize(window.innerWidth + 48);
      };
      updateSize();
      window.addEventListener('resize', updateSize);
      return () => window.removeEventListener('resize', updateSize);
    }
  }, []);

  // Load Hebrew date data (original system)
  useEffect(() => {
    const hDate = getHebrewDate();
    setHebrewDate(hDate);
    setMoonPhase(getMoonPhase());
    
    // Get days in current Hebrew month
    const today = new HDate();
    const daysInCurrentMonth = today.daysInMonth();
    setDaysInMonth(daysInCurrentMonth);
    
    // Check if we're showing today
    setIsToday(true); // For now, always true since we're using getHebrewDate() which gets today
    
    const holidays = getUpcomingHolidays(20);
    // Find first major holiday or any significant holiday
    const majorHoliday = holidays.find(h => h.isMajorHoliday && !h.name.toLowerCase().includes('rosh chodesh'));
    const significantHoliday = holidays.find(h => !h.name.toLowerCase().includes('rosh chodesh'));
    setNextHoliday(majorHoliday || significantHoliday || holidays[0]);
    
    // Mark that initial animation has played after a short delay
    setTimeout(() => {
      setHasAnimated(true);
    }, 500);
  }, []);

  // Get current date based on day offset
  const getCurrentDate = () => {
    if (!hebrewDate) return null;
    
    const today = new HDate();
    const targetDate = new HDate(today);
    targetDate.add(dayOffset);
    
    const day = targetDate.getDate();
    const rawMonth = targetDate.getMonthName('h');
    const month = normalizeMonthName(rawMonth); // Normalize to standardized spelling
    const monthName = normalizeMonthName(rawMonth);
    const monthHebrew = HEBREW_MONTHS[monthName] || HEBREW_MONTHS[rawMonth] || monthName;
    const year = targetDate.getFullYear();
    
    // Convert to Hebrew letters
    const dayHebrew = numberToHebrewLetters(day);
    const yearHebrew = yearToHebrewLetters(year);
    
    const gregorianDate = targetDate.greg();
    
    return {
      day: day,
      month: month,
      monthHebrew: monthHebrew,
      year: year,
      dayOfWeek: gregorianDate.toLocaleDateString('en-US', { weekday: 'long' }),
      formatted: `${gregorianDate.toLocaleDateString('en-US', { weekday: 'long' })}, ${day} ${month} ${year}`,
      shortFormatted: `${day} ${month} ${year}`,
      hebrewFormatted: `${dayHebrew} ${monthHebrew} ${yearHebrew}`,
      dayHebrew: dayHebrew,
      yearHebrew: yearHebrew,
      gregorianDate: gregorianDate
    };
  };

  // Get current date for display (uses dayOffset) - recalculate when dayOffset changes
  const currentDate = useMemo(() => {
    if (!hebrewDate) {
      return null;
    }
    
    // Get today's Hebrew date, convert to Gregorian, add days, then convert back
    const today = new HDate();
    const todayGregorian = today.greg();
    
    // Add days to Gregorian date
    const targetGregorian = new Date(todayGregorian);
    targetGregorian.setDate(targetGregorian.getDate() + dayOffset);
    
    // Convert back to Hebrew date
    const targetDate = new HDate(targetGregorian);
    
    const day = targetDate.getDate();
    const rawMonth = targetDate.getMonthName('h');
    const month = normalizeMonthName(rawMonth); // Normalize to standardized spelling
    const monthName = normalizeMonthName(rawMonth);
    const monthHebrew = HEBREW_MONTHS[monthName] || HEBREW_MONTHS[rawMonth] || monthName;
    const year = targetDate.getFullYear();
    
    // Convert to Hebrew letters
    const dayHebrew = numberToHebrewLetters(day);
    const yearHebrew = yearToHebrewLetters(year);
    
    const gregorianDate = targetDate.greg();
    
    const date = {
      day: day,
      month: month,
      monthHebrew: monthHebrew,
      year: year,
      dayOfWeek: gregorianDate.toLocaleDateString('en-US', { weekday: 'long' }),
      formatted: `${gregorianDate.toLocaleDateString('en-US', { weekday: 'long' })}, ${day} ${month} ${year}`,
      shortFormatted: `${day} ${month} ${year}`,
      hebrewFormatted: `${dayHebrew} ${monthHebrew} ${yearHebrew}`,
      dayHebrew: dayHebrew,
      yearHebrew: yearHebrew,
      gregorianDate: gregorianDate
    };
    
    return date;
  }, [dayOffset, hebrewDate]);
  const displayDate = currentDate || hebrewDate;

  // Get Gregorian date for display
  const getGregorianDate = () => {
    if (!displayDate) return '';
    const gregDate = currentDate?.gregorianDate || (new HDate()).greg();
    return gregDate.toLocaleDateString('en-US', { 
      month: 'long', 
      day: 'numeric', 
      year: 'numeric' 
    });
  };
  
  // Convert day offset to angle
  // Hebrew year has 354 days, but orrery completes 360 degrees
  // So 1 day = 360/354 degrees ≈ 1.0169 degrees
  const dayOffsetToAngle = (dayOffset) => {
    const initialAngle = calculateInitialAngle();
    // Each day = 360/354 degrees of earth orbit (to account for 354-day year)
    const degreesPerDay = 360 / 354;
    return normalizeAngle(initialAngle + dayOffset * degreesPerDay);
  };

  // Convert angle to day offset (for scrubbing, doesn't normalize to avoid year wrap-around)
  const angleToDayOffsetForScrubbing = (angle) => {
    const initialAngle = calculateInitialAngle();
    // Calculate raw difference (can be negative, can be > 180 or < -180)
    // Don't normalize to avoid year wrap-around during scrubbing
    const angleDelta = angle - initialAngle;
    // Hebrew year has 354 days, but orrery completes 360 degrees
    // So 1 degree = 354/360 days ≈ 0.9833 days
    const daysPerDegree = 354 / 360;
    return Math.round(angleDelta * daysPerDegree);
  };

  // Convert angle to day offset
  // Handles negative offsets correctly by finding shortest angular distance
  const angleToDayOffset = (angle) => {
    const initialAngle = calculateInitialAngle();
    // Calculate raw difference (can be negative)
    let angleDelta = angle - initialAngle;
    
    // Normalize to -180 to +180 range (shortest path)
    // This ensures backward drag gives negative offset
    while (angleDelta > 180) angleDelta -= 360;
    while (angleDelta < -180) angleDelta += 360;
    
    // Hebrew year has 354 days, but orrery completes 360 degrees
    // So 1 degree = 354/360 days ≈ 0.9833 days
    const daysPerDegree = 354 / 360;
    return Math.round(angleDelta * daysPerDegree);
  };

  // Handle day change from moon drag - sync with orrery
  const handleDayChange = (offset) => {
    setDayOffset(offset);
    // Update accumulated tracking for moon drag
    accumulatedDayOffsetRef.current = offset;
    setIsToday(offset === 0);
    // Update orrery angle to match - use the new offset value, not the old state
    const newAngle = dayOffsetToAngle(offset);
    setCurrentAngle(newAngle);
    // Update previous angle ref to match
    previousAngleRef.current = newAngle;
  };

  // Handle angle change from orrery drag - sync with moon
  // Uses incremental tracking to prevent year wrap-around during scrubbing
  const handleAngleChange = (angle) => {
    setCurrentAngle(angle);
    
    // Calculate incremental angle change (handles wraparound correctly)
    const prevAngle = previousAngleRef.current;
    let angleDelta = angle - prevAngle;
    
    // Handle wraparound: if angle goes from ~360 to ~0 (or vice versa), 
    // we need to detect the direction of rotation
    // Use a more precise threshold to catch wraparounds accurately
    if (Math.abs(angleDelta) > 180) {
      // Determine direction based on which path is shorter
      if (angleDelta > 180) {
        // Wrapped backwards (e.g., 350 -> 10 should be -20, not +20)
        angleDelta -= 360;
      } else if (angleDelta < -180) {
        // Wrapped forwards (e.g., 10 -> 350 should be +20, not -20)
        angleDelta += 360;
      }
    }
    
    // Accumulate the unnormalized angle delta to maintain precision
    // This ensures symmetric behavior for forward and backward scrubbing
    cumulativeAngleDeltaRef.current += angleDelta;
    
    // Calculate effective angle from initial angle + cumulative delta
    const initialAngle = calculateInitialAngle();
    const effectiveAngle = initialAngle + cumulativeAngleDeltaRef.current;
    
    // Use angleToDayOffsetForScrubbing to convert effective angle to day offset (accounts for compensation)
    const newDayOffset = angleToDayOffsetForScrubbing(effectiveAngle);
    
    // Update refs
    previousAngleRef.current = angle;
    accumulatedDayOffsetRef.current = newDayOffset;
    
    setDayOffset(newDayOffset);
    setIsToday(newDayOffset === 0);
  };

  // Handle holiday jump - set dayOffset directly (bypasses angleToDayOffset which breaks for large jumps)
  const handleHolidayJump = (dayOffset, angle) => {
    setCurrentAngle(angle);
    setDayOffset(dayOffset);
    // Reset accumulated tracking for holiday jumps
    previousAngleRef.current = angle;
    accumulatedDayOffsetRef.current = dayOffset;
    cumulativeAngleDeltaRef.current = dayOffset; // Reset cumulative delta to match dayOffset
    setIsToday(dayOffset === 0);
  };
  

  // Update daysInMonth when date changes
  useEffect(() => {
    if (currentDate) {
      // Use the same approach: convert to Gregorian, add days, convert back
      const today = new HDate();
      const todayGregorian = today.greg();
      const targetGregorian = new Date(todayGregorian);
      targetGregorian.setDate(targetGregorian.getDate() + dayOffset);
      const targetDate = new HDate(targetGregorian);
      setDaysInMonth(targetDate.daysInMonth());
    }
  }, [dayOffset, currentDate]);

  // Reset to today
  const resetToToday = () => {
    setDayOffset(0);
    const hDate = getHebrewDate();
    setHebrewDate(hDate);
    const today = new HDate();
    setDaysInMonth(today.daysInMonth());
    const initialAngle = calculateInitialAngle();
    setCurrentAngle(initialAngle);
    setIsToday(true);
    // Reset accumulated tracking
    previousAngleRef.current = initialAngle;
    accumulatedDayOffsetRef.current = 0;
    cumulativeAngleDeltaRef.current = 0; // Reset cumulative angle delta
    // Force update of currentDate to trigger moon image refresh
    // The currentDate useMemo will recalculate when dayOffset changes to 0
  };

  if (!hebrewDate) return null;

  return (
    <div className="bg-gradient-to-b from-background to-white">
      {/* Desktop: 3-column layout with centered date block */}
      <div className="hidden md:flex md:items-center md:justify-center max-w-7xl mx-auto px-8 py-6">
        <div className="flex items-center justify-center" style={{ gap: '64px' }}>
        
        {/* Column 1: Original Draggable Moon - 20% bigger */}
        <div className="flex flex-col items-center justify-center flex-shrink-0">
            <DraggableMoon
              initialHebrewDay={hebrewDate.day}
              daysInMonth={daysInMonth}
              size="235px"
              maxSize="235px"
              onDayChange={handleDayChange}
              currentDayOffset={dayOffset}
              currentHebrewDay={currentDate?.day || hebrewDate.day}
            />
        </div>

        {/* Column 2: Date Display (Centered) */}
        <div className="flex flex-col items-center justify-center flex-shrink-0">
          <motion.div
            initial={!hasAnimated ? { opacity: 0, y: 20 } : false}
            animate={!hasAnimated ? { opacity: 1, y: 0 } : {}}
            className="text-center"
          >
            {/* Hebrew Date (Transliterated) - Original Size */}
            <div className="flex items-center justify-center gap-2 mb-1" key={`date-short-${dayOffset}-${currentDate?.shortFormatted}`}>
              <h1 
                className="text-xl md:text-2xl font-serif md:font-sans font-bold md:font-semibold text-text-primary leading-tight tracking-tighter"
                style={{ letterSpacing: '-0.05em' }}
              >
                {currentDate?.shortFormatted || hebrewDate.shortFormatted}
              </h1>
            </div>

            {/* Hebrew Date with Gematria tooltip - Closer spacing, smaller */}
            <p className="text-base text-text-secondary mb-2 font-serif" dir="rtl" style={{ marginTop: '0.25rem' }} key={`date-hebrew-${dayOffset}-${currentDate?.hebrewFormatted}`}>
              <GematriaTooltip explanation="Hebrew dates use letters instead of numbers. Each letter has a numerical value: א=1, ב=2, ג=3, ד=4, ה=5, ו=6, ז=7, ח=8, ט=9, י=10, כ=20, ל=30, מ=40, נ=50, ס=60, ע=70, פ=80, צ=90, ק=100, ר=200, ש=300, ת=400. Days 15 and 16 use טו and טז to avoid spelling God's name. Years typically omit the thousands digit (5) and show only the remainder (e.g., 5786 becomes תשפ״ו for 786). The geresh (״) after the year indicates it's a number. This system is called gematria (גימטריה).">
                {currentDate?.hebrewFormatted || hebrewDate.hebrewFormatted}
              </GematriaTooltip>
            </p>

            {/* Gregorian Date */}
            <p className="text-base text-text-secondary mb-3" key={`date-gregorian-${dayOffset}-${currentDate?.gregorianDate?.toLocaleDateString()}`}>
              {currentDate ? currentDate.gregorianDate.toLocaleDateString('en-US', { 
                month: 'long', 
                day: 'numeric', 
                year: 'numeric' 
              }) : getGregorianDate()}
            </p>
          </motion.div>

          {/* Return to Today Button - Always visible, using moon phase tag styling - Smaller */}
          <button
            onClick={resetToToday}
            className="inline-flex items-center gap-1 bg-white/60 backdrop-blur-sm px-2.5 py-1 rounded-full border border-border/50 shadow-sm hover:bg-white/80 transition-all min-w-[220px] justify-center"
          >
            {isToday ? (
              <div className="w-1.5 h-1.5 rounded-full bg-primary" />
            ) : (
              <FontAwesomeIcon icon={faRotateRight} className="w-2 h-2" style={{ color: PRIMARY_COLOR }} />
            )}
            <span className="text-xs font-semibold text-text-primary">
              {isToday ? 'Today' : (
                <>
                  <span className="text-text-primary">
                    {dayOffset < 0 
                      ? `${Math.abs(dayOffset)} day${Math.abs(dayOffset) !== 1 ? 's' : ''} ago`
                      : `${dayOffset} day${dayOffset !== 1 ? 's' : ''} from now`
                    }
                  </span>
                  <span style={{ color: PRIMARY_COLOR }}> (reset)</span>
                </>
              )}
            </span>
          </button>
        </div>

        {/* Column 3: Interactive Orrery - 20% bigger */}
        <div className="flex flex-col items-center justify-center flex-shrink-0">
          <InteractiveOrrery
            currentAngle={currentAngle}
            onAngleChange={handleAngleChange}
            onHolidayJump={handleHolidayJump}
            size={360}
            showNextHoliday={true}
          />
        </div>
        </div>
      </div>

      {/* Mobile: Stacked layout */}
      <div className="md:hidden px-4 py-4 space-y-4">
        {/* Moon - Original DraggableMoon */}
        <div className="flex justify-center pt-8 pb-2">
          <DraggableMoon
            key={`moon-mobile-${hebrewDate.day}-${dayOffset}`}
            initialHebrewDay={hebrewDate.day}
            daysInMonth={daysInMonth}
            size="56vw"
            maxSize="336px"
            onDayChange={handleDayChange}
            currentDayOffset={dayOffset}
            currentHebrewDay={currentDate?.day || hebrewDate.day}
          />
        </div>

        {/* Date Display (Original Style) */}
        <div className="text-center space-y-1.5">
          {/* Hebrew Date (Transliterated) - Original Size */}
          <div className="flex items-center justify-center gap-2 mb-1" key={`mobile-date-short-${dayOffset}-${currentDate?.shortFormatted}`}>
            <h1 
              className="text-xl font-serif font-bold text-text-primary leading-tight"
              style={{ letterSpacing: '-0.02em' }}
            >
              {currentDate?.shortFormatted || hebrewDate.shortFormatted}
            </h1>
          </div>

          {/* Hebrew Date with Gematria - Smaller */}
          <p className="text-base text-text-secondary mb-2 font-serif" dir="rtl" style={{ marginTop: '0.05rem' }} key={`mobile-date-hebrew-${dayOffset}-${currentDate?.hebrewFormatted}`}>
            <GematriaTooltip explanation="Hebrew dates use letters instead of numbers. Each letter has a numerical value: א=1, ב=2, ג=3, ד=4, ה=5, ו=6, ז=7, ח=8, ט=9, י=10, כ=20, ל=30, מ=40, נ=50, ס=60, ע=70, פ=80, צ=90, ק=100, ר=200, ש=300, ת=400. Days 15 and 16 use טו and טז to avoid spelling God's name. Years typically omit the thousands digit (5) and show only the remainder (e.g., 5786 becomes תשפ״ו for 786). The geresh (״) after the year indicates it's a number. This system is called gematria (גימטריה).">
              {currentDate?.hebrewFormatted || hebrewDate.hebrewFormatted}
            </GematriaTooltip>
          </p>

          {/* Gregorian Date */}
          <p className="text-base text-text-secondary mb-3" key={`mobile-date-gregorian-${dayOffset}-${currentDate?.gregorianDate?.toLocaleDateString()}`}>
            {currentDate ? currentDate.gregorianDate.toLocaleDateString('en-US', { 
              month: 'long', 
              day: 'numeric', 
              year: 'numeric' 
            }) : getGregorianDate()}
          </p>

          {/* Return to Today Button - Always visible, using moon phase tag styling - Smaller */}
          <button
            onClick={resetToToday}
            className="inline-flex items-center gap-1 bg-white/60 backdrop-blur-sm px-2.5 py-1 rounded-full border border-border/50 shadow-sm hover:bg-white/80 transition-all justify-center relative z-50"
            style={{ pointerEvents: 'auto', width: 'fit-content' }}
          >
            {isToday ? (
              <div className="w-1.5 h-1.5 rounded-full bg-primary" />
            ) : (
              <FontAwesomeIcon icon={faRotateRight} className="w-2 h-2" style={{ color: PRIMARY_COLOR }} />
            )}
            <span className="text-xs font-semibold text-text-primary">
              {isToday ? 'Today' : (
                <>
                  <span className="text-text-primary">
                    {dayOffset < 0 
                      ? `${Math.abs(dayOffset)} day${Math.abs(dayOffset) !== 1 ? 's' : ''} ago`
                      : `${dayOffset} day${dayOffset !== 1 ? 's' : ''} from now`
                    }
                  </span>
                  <span style={{ color: PRIMARY_COLOR }}> (reset)</span>
                </>
              )}
            </span>
          </button>
        </div>

        {/* Orrery - Full width on mobile */}
        <div className="flex justify-center" style={{ width: '100vw', marginLeft: '-24px', marginRight: '-24px', marginTop: '-60px', marginBottom: '-40px' }}>
          <InteractiveOrrery
            currentAngle={currentAngle}
            onAngleChange={handleAngleChange}
            onHolidayJump={handleHolidayJump}
            size={mobileOrrerySize}
            showNextHoliday={true}
            isMobile={true}
          />
        </div>
      </div>

    </div>
  );
}
